import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AppLoader from '../component/AppLoader';
import Actions from '../Redux/action';
import moment from 'moment/moment';
import * as FileSaver from 'file-saver';
import ExcelJS from 'exceljs';
import { toast } from 'react-toastify';
import {
  getHealthCheckDataSelector,
  getHealthCheckHostelsSelector,
  getHealthCheckLoadingSelector,
  getDowntimeReportLoadingSelector,
} from '../Redux/reducer/HealthCheckReducer';
import nodatafound from '../asset/img/datanotfound_black.png';

const EXCEL_EXTENSION = '.xlsx';
const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

export const HealthCheck = () => {
  const dispatch = useDispatch();
  const healthCheckData = useSelector(getHealthCheckDataSelector);
  const hostels = useSelector(getHealthCheckHostelsSelector);
  const loading = useSelector(getHealthCheckLoadingSelector);
  const downtimeReportLoading = useSelector(getDowntimeReportLoadingSelector);
  const [sortColumn, setSortColumn] = useState(null);
  const [downtimeDays, setDowntimeDays] = useState(30);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedHostelId, setSelectedHostelId] = useState('');
  const defaultHostelSetRef = useRef(false);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({ column, label }) => (
    <th
      className="border-0 cursor-pointer user-select-none"
      onClick={() => handleSort(column)}
      style={{ cursor: 'pointer' }}
    >
      {label}
      {sortColumn === column && (
        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
      )}
      {sortColumn !== column && (
        <i className="fas fa-sort ms-1 text-muted" style={{ opacity: 0.5 }}></i>
      )}
    </th>
  );

  const fetchHealthCheckData = useCallback(() => {
    dispatch(Actions.getHealthCheckData());
  }, [dispatch]);

  const fetchDowntimeReport = useCallback((days, hostelId, callback) => {
    const params = { days };
    if (hostelId) params.hostel_id = hostelId;
    dispatch(Actions.getDowntimeReport(params, callback));
  }, [dispatch]);

  useEffect(() => {
    fetchHealthCheckData();
  }, [fetchHealthCheckData]);

  useEffect(() => {
    if (hostels?.length > 0 && !defaultHostelSetRef.current) {
      defaultHostelSetRef.current = true;
      setSelectedHostelId(String(hostels[0].id));
    }
  }, [hostels]);

  const sanitizeSheetName = (name) => {
    if (!name || name === '-') return 'Other';
    const sanitized = String(name).replace(/[:\\/?*[\]]/g, ' ').trim();
    return sanitized.slice(0, 31) || 'Other';
  };

  const downloadDowntimeReport = useCallback(async () => {
    const hostelId = selectedHostelId || (hostels && hostels[0]?.id);
    if (!hostelId) {
      toast.error('Please select a hostel to download the downtime report.');
      return;
    }
    fetchDowntimeReport(downtimeDays, hostelId, async (response) => {
      if (response instanceof Error || (response && response.status_code !== 0)) {
        toast.error(response?.message || 'Failed to load downtime report.');
        return;
      }
      const list = response?.response;
      if (!Array.isArray(list) || list.length === 0) {
        toast.info('No downtime records in the selected period for this hostel.');
        return;
      }
      const deviceIdToDisplayName = {};
      (healthCheckData || []).forEach((d) => { deviceIdToDisplayName[d.id] = d.name; });
      const periodLabel = `Last ${downtimeDays} days`;
      const workbook = new ExcelJS.Workbook();
      const resolvedHostelName = hostels?.find((h) => String(h.id) === String(hostelId))?.name
        || list[0]?.hostel_name
        || 'Hostel';

      const cameraRows = [];
      const turnstileRows = [];
      list.forEach((row) => {
        const deviceDisplayName = deviceIdToDisplayName[row.device_id] || row.device_name || `Device ${row.device_id}`;
        const deviceType = row.device_type != null ? String(row.device_type) : '';
        const rowData = {
          Device: deviceDisplayName,
          'Downtime Start': row.period_start ? moment(row.period_start).format('DD-MM-YYYY HH:mm') : '-',
          'Downtime End': row.still_down ? 'Ongoing' : (row.period_end ? moment(row.period_end).format('DD-MM-YYYY HH:mm') : '-'),
          'Downtime Duration': row.duration_minutes != null ? row.duration_minutes : '-',
        };
        if (deviceType === '1') {
          cameraRows.push(rowData);
        } else {
          turnstileRows.push(rowData);
        }
      });

      const colHeaders = ['Device', 'Downtime Start', 'Downtime End', 'Downtime Duration'];
      const colCount = colHeaders.length;

      const addSheet = (sheetName, rows) => {
        if (rows.length === 0) return;
        const worksheet = workbook.addWorksheet(sheetName, { views: [{ rightToLeft: false }] });

        worksheet.mergeCells(1, 1, 1, colCount);
        worksheet.getCell(1, 1).value = resolvedHostelName;
        worksheet.getCell(1, 1).font = { bold: true };
        worksheet.getCell(1, 1).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.mergeCells(2, 1, 2, colCount);
        worksheet.getCell(2, 1).value = periodLabel;
        worksheet.getCell(2, 1).font = { bold: true };
        worksheet.getCell(2, 1).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.addRow(colHeaders);
        const headerRow = worksheet.getRow(3);
        colHeaders.forEach((_, i) => {
          headerRow.getCell(i + 1).font = { bold: true };
          headerRow.getCell(i + 1).alignment = { horizontal: 'center', vertical: 'middle' };
        });
        rows.forEach((r) => worksheet.addRow([r.Device, r['Downtime Start'], r['Downtime End'], r['Downtime Duration']]));
        worksheet.columns = [{ width: 22 }, { width: 20 }, { width: 20 }, { width: 18 }];
      };

      addSheet('Cameras', cameraRows);
      addSheet('Turnstiles', turnstileRows);

      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: EXCEL_TYPE });
      FileSaver.saveAs(data, `downtime_report_${sanitizeSheetName(resolvedHostelName)}_${downtimeDays}days_${Date.now()}${EXCEL_EXTENSION}`);
      toast.success('Downtime report downloaded.');
    });
  }, [fetchDowntimeReport, downtimeDays, selectedHostelId, hostels, healthCheckData]);

  const getDeviceTypeName = (deviceType) => {
    if (deviceType === undefined || deviceType === null) return 'Unknown';
    
    switch(String(deviceType)) {
      case '1':
        return 'Camera';
      case '2':
        return 'Turnstile';
      default:
        return 'Unknown';
    }
  };

  const getHealthStatusName = (health) => {
    if (health === undefined || health === null) return 'Unknown';
    
    switch(String(health)) {
      case '0':
        return 'Online';
      case '1':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };

  const getHealthBadgeClass = (health) => {
    if (health === undefined || health === null) return 'badge bg-light text-dark';
    
    switch(String(health)) {
      case '0':
        return 'badge bg-light text-success border border-success';
      case '1':
        return 'badge bg-light text-danger border border-danger';
      default:
        return 'badge bg-light text-dark border';
    }
  };

  const getDeviceData = () => {
    if (!healthCheckData) return [];
    const list = Array.isArray(healthCheckData) ? healthCheckData : [];
    if (!selectedHostelId) return list;
    const hid = Number(selectedHostelId);
    return list.filter((d) => Array.isArray(d.hostel_ids) && d.hostel_ids.includes(hid));
  };

  const sortedDeviceData = useMemo(() => {
    const data = getDeviceData();
    if (!sortColumn || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === 'hostel') {
        aVal = (a.hostel_name || '-').toLowerCase();
        bVal = (b.hostel_name || '-').toLowerCase();
      } else if (sortColumn === 'status') {
        aVal = getHealthStatusName(a.health);
        bVal = getHealthStatusName(b.health);
      } else if (sortColumn === 'type') {
        aVal = getDeviceTypeName(a.device_type);
        bVal = getDeviceTypeName(b.device_type);
      } else if (sortColumn === 'onlineSince') {
        aVal = a.last_updated ? new Date(a.last_updated).getTime() : 0;
        bVal = b.last_updated ? new Date(b.last_updated).getTime() : 0;
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return sortDirection === 'asc' ? cmp : -cmp;
      } else {
        return 0;
      }
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [healthCheckData, selectedHostelId, sortColumn, sortDirection]);

  const renderDeviceRow = (device, index) => {
    if (!device) return null;
    
    const deviceTypeName = getDeviceTypeName(device.device_type);
    const healthStatus = getHealthStatusName(device.health);
    const badgeClass = getHealthBadgeClass(device.health);
    const lastUpdated = device.last_updated ? 
      moment(device.last_updated).format('DD-MM-YYYY HH:mm') : 
      'Never';

    return (
      <tr key={device.id || index}>
        <td className="text-muted">{index + 1}</td>
        <td>
          <div className="fw-semibold">{device.name || `Device ${device.id || index + 1}`}</div>
        </td>
        <td>
          <span className="badge bg-light text-dark border">
            {deviceTypeName}
          </span>
        </td>
        <td>
          <span className={badgeClass}>
            {healthStatus}
          </span>
        </td>
        <td className="text-muted">{device.hostel_name || '-'}</td>
        <td className="text-muted">{lastUpdated}</td>
      </tr>
    );
  };

  return (
    <>
      {loading && <AppLoader />}
      
      <div className="container-fluid">
        {/* Header + Hostel filter */}
        <div className="mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h5 className="card-title mb-2">Health Check</h5>
            <p className="text-muted mb-0">Device status monitoring</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="text-muted small mb-0">Hostel</label>
            <select
              className="form-select form-select-sm"
              style={{ minWidth: '180px' }}
              value={selectedHostelId}
              onChange={(e) => setSelectedHostelId(e.target.value)}
            >
              {/* <option value="">All hostels</option> */}
              {Array.isArray(hostels) && hostels.map((h) => (
                <option key={h.id} value={h.id}>{h.name || `Hostel ${h.id}`}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Downtime Report - Download for selected hostel only */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h5 className="card-title mb-1">Downtime Report</h5>
                <p className="text-muted small mb-0">
                  Download offline/error periods for the selected hostel (one sheet per device). Select a hostel above.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="text-muted small mb-0">Last</label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={downtimeDays}
                  onChange={(e) => setDowntimeDays(Number(e.target.value))}
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={15}>15 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                </select>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={downloadDowntimeReport}
                  disabled={downtimeReportLoading}
                >
                  {downtimeReportLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      Downloading…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download me-1" />
                      Download
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Devices Table (filtered by selected hostel) */}
        <div className="alert alert-info py-2 px-3 mb-3 small">
          <i className="fas fa-info-circle me-2" />
          Note: Turnstile numbers are arranged in consecutive order from entry right turnstile to left.
        </div>
        <div className="card">
          <div className="card-body p-0">
            {sortedDeviceData.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0">#</th>
                      <th className="border-0">Device Name</th>
                      <SortableHeader column="type" label="Type" />
                      <SortableHeader column="status" label="Status" />
                      <SortableHeader column="hostel" label="Hostel" />
                      <SortableHeader column="onlineSince" label="Online since" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDeviceData.map((device, index) => renderDeviceRow(device, index))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5">
                <img 
                  src={nodatafound} 
                  alt="No Data Found" 
                  className="img-fluid mb-3"
                  style={{maxWidth: '200px', opacity: 0.5}}
                />
                <h4 className="text-muted">No Devices Found</h4>
                <p className="text-muted">
                  {loading ? 'Loading health check data...' : 'No health check data available'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};